import React from "react";
import { Badge } from "../../../components/ui/badge";

// EMAILS-IMPL-007: renders exactly the backend-governed status vocabulary
// (email_marketing_catalog.py's CampaignStatus/AngleStatus/
// AudienceDefinitionStatus Literals) -- never an invented status name.
// Program has no governed lifecycle vocabulary at all (see
// ProgramsPanel.jsx), so `status` may legitimately be null/undefined here;
// that renders as a plain muted dash, never a fabricated "Draft".
//
// EMAILS-IMPL-009: added "Control" -- the 5th state of the 5-state Asset
// family vocabulary (Subject/Preview/Hook/Landing Page/Image: Draft/
// Review/Approved/Control/Retired; see email_marketing_assets.py). Every
// other Asset/Campaign/Angle/AudienceDefinition status name was already
// covered by the styles below.
//
// EMAILS-IMPL-011: added "Challenger" -- the one state unique to the
// Creative-specific 7-state vocabulary (Draft/Review/Approved/Control/
// Challenger/Retired/Archived; see email_marketing_creatives.py's own
// CreativeStatus). No other family's vocabulary has both "Control" and
// "Challenger" -- this component still renders exactly the status string
// the backend returns, never inferring/normalizing across families.
//
// EMAILS-IMPL-020: added "Scheduled"/"Sending"/"Completed"/"Cancelled" --
// the four states unique to Broadcast's own 8-state vocabulary (Draft/
// Review/Approved/Scheduled/Sending/Completed/Archived/Cancelled; see
// email_marketing_broadcasts.BroadcastStatus). Broadcast is the only
// family with "Sending"/"Completed"/"Cancelled" -- never conflated with
// any other family's vocabulary, same discipline as every prior addition.
//
// EMAILS-IMPL-024 (BRD-SPEC-001 v2.6.0 / EMAILS-SPEC-015): added "Launch
// Reported" -- the prospective replacement for "Sending"/"Completed"
// (email_marketing_broadcasts.BroadcastStatus). "Sending"/"Completed"
// remain in this map UNCHANGED and un-removed: they are still real,
// renderable values on any legacy Broadcast record (BRD-SPEC-001 §21.3),
// even though the authoring service no longer accepts either as a
// prospective PATCH target -- this component renders whatever status
// string the backend returns and does not know or care whether that
// value is prospective or legacy. "Launch Reported" deliberately does
// NOT reuse Approved's emerald ("success"-coded) treatment, or Sending/
// Completed's own colors -- blue reads as a neutral "an assertion was
// recorded" signal, never a claim of verified GHL/SendGrid delivery.
const STATUS_STYLES = {
  Draft: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  Review: "bg-sky-500/15 text-sky-300 border-sky-400/30",
  Approved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  Active: "bg-violet-500/15 text-violet-300 border-violet-400/30",
  Control: "bg-indigo-500/15 text-indigo-300 border-indigo-400/30",
  Challenger: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/30",
  Scheduled: "bg-cyan-500/15 text-cyan-300 border-cyan-400/30",
  "Launch Reported": "bg-blue-500/15 text-blue-300 border-blue-400/30",
  Sending: "bg-orange-500/15 text-orange-300 border-orange-400/30",
  Completed: "bg-teal-500/15 text-teal-300 border-teal-400/30",
  Cancelled: "bg-rose-500/15 text-rose-300 border-rose-400/30",
  Retired: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  Deprecated: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  Archived: "bg-zinc-500/15 text-zinc-400 border-zinc-400/30",
};

export default function StatusBadge({ status, testId }) {
  if (!status) {
    return (
      <span className="text-sm text-slate-500" data-testid={testId}>
        —
      </span>
    );
  }

  const className = STATUS_STYLES[status] || "bg-slate-500/15 text-slate-300 border-slate-400/30";

  return (
    <Badge variant="outline" className={className} data-testid={testId}>
      {status}
    </Badge>
  );
}
